import { useAuthStore } from '@/stores/auth';

/**
 * Unified Streaming HTTP Service
 * Provides utilities for handling streaming responses with optional resilient mode
 */
class StreamService {
    constructor() {
        this.baseURL = import.meta.env.VITE_API_PLATFORM_ENTRYPOINT || '';
        this.activeSessions = new Map(); // Track active streaming sessions for resilient mode
        this.retryCounters = new Map(); // Track retry counters per session
        this.bytesReceived = new Map(); // Track total bytes received per session
    }

    /**
     * Make a streaming POST request with optional resilient mode
     * @param {string} endpoint - API endpoint (relative path)
     * @param {Object} data - Request body data
     * @param {Function} onProgress - Callback function called for each streaming chunk
     * @param {Object} options - Additional fetch options
     * @param {boolean} options.resilient - Enable resilient mode with auto-reconnection (default: false)
     * @param {number} options.maxRetries - Maximum retry attempts in resilient mode (default: 5)
     * @param {number} options.retryDelay - Delay between retries in ms (default: 2000)
     * @param {string} options.sessionId - Session ID for tracking (auto-generated if not provided)
     * @returns {Promise} Promise that resolves when stream is complete
     */
    async streamPost(endpoint, data, onProgress, options = {}) {
        // Check if resilient mode is enabled
        if (options.resilient === true) {
            return this._streamWithRetry('POST', endpoint, data, onProgress, options);
        }
        // Default non-resilient streaming
        return this._streamRequest('POST', endpoint, data, onProgress, options);
    }

    /**
     * Make a streaming GET request with optional resilient mode
     * @param {string} endpoint - API endpoint (relative path)
     * @param {Function} onProgress - Callback function called for each streaming chunk
     * @param {Object} options - Additional fetch options
     * @returns {Promise} Promise that resolves when stream is complete
     */
    async streamGet(endpoint, onProgress, options = {}) {
        // Check if resilient mode is enabled
        if (options.resilient === true) {
            return this._streamWithRetry('GET', endpoint, null, onProgress, options);
        }

        // Default non-resilient streaming
        return this._streamRequest('GET', endpoint, null, onProgress, options);
    }

    /**
     * Generate a unique session ID for tracking streaming sessions
     * @private
     * @returns {string} Unique session identifier
     */
    _generateSessionId() {
        return Date.now().toString(36) + Math.random().toString(36).substr(2);
    }

    /**
     * Stream with automatic retry and reconnection logic (resilient mode)
     * @private
     */
    async _streamWithRetry(method, endpoint, data, onProgress, options = {}) {
        const { maxRetries = 5, retryDelay = 2000 } = options;
        const sessionId = options.sessionId || this._generateSessionId();

        // Initialize session tracking
        this.retryCounters.set(sessionId, 0);
        this.bytesReceived.set(sessionId, 0);

        // Store session info for potential resumption
        this.activeSessions.set(sessionId, {
            endpoint,
            data,
            bytesReceived: 0,
            startTime: Date.now(),
        });

        // Keep streaming with automatic retry reset after successful reconnections
        while (true) {
            let connectionLostThisRound = false;

            // Try up to maxRetries for this connection session
            while (this.retryCounters.get(sessionId) <= maxRetries) {
                try {
                    const currentRetries = this.retryCounters.get(sessionId);

                    // Notify about connection attempt
                    if (currentRetries > 0) {
                        onProgress(`<div class="console-message console-info">
<div class="console-message-header">RECONNECTING</div>
<div class="console-message-content">Attempting to reconnect... (attempt ${currentRetries}/${maxRetries})</div>
<div class="console-message-content">Session: ${sessionId.substr(0, 8)}...</div>
</div>`);
                    }

                    // Show reconnected message immediately if this was after a connection loss
                    if (connectionLostThisRound && currentRetries > 0) {
                        const currentBytes = this.bytesReceived.get(sessionId);
                        onProgress(`<div class="console-message console-success">
<div class="console-message-header">RECONNECTED</div>
<div class="console-message-content">Connection restored and streaming resumed successfully</div>
<div class="console-message-content">Resuming from byte position: ${currentBytes}</div>
</div>`);
                    }

                    const resumeAfter = this.bytesReceived.get(sessionId);

                    await this._streamRequest(method, endpoint, data, onProgress, {
                        ...options,
                        sessionId,
                        resumeAfter,
                    });

                    // Stream completed successfully - clean up and exit
                    this.activeSessions.delete(sessionId);
                    this.retryCounters.delete(sessionId);
                    this.bytesReceived.delete(sessionId);
                    return; // Success - exit all retry loops
                } catch (error) {
                    if (error?.name === 'AbortError') {
                        this.cancelSession(sessionId);
                        throw error;
                    }
                    connectionLostThisRound = true;

                    // Increment retry counter for this session
                    const currentRetries = this.retryCounters.get(sessionId) + 1;
                    this.retryCounters.set(sessionId, currentRetries);

                    console.error(`Stream attempt ${currentRetries} failed:`, error);

                    if (currentRetries <= maxRetries) {
                        const currentBytes = this.bytesReceived.get(sessionId);
                        onProgress(`<div class="console-message console-warning">
<div class="console-message-header">CONNECTION LOST</div>
<div class="console-message-content">Network error: ${error.message}</div>
<div class="console-message-content">Retrying in ${retryDelay / 1000} seconds... (${currentRetries}/${maxRetries})</div>
<div class="console-message-content">Session: ${sessionId.substr(0, 8)}... | Bytes received: ${currentBytes}</div>
</div>`);

                        // Wait before retrying with exponential backoff
                        const delay = retryDelay * Math.pow(1.5, currentRetries - 1);
                        await new Promise((resolve) => setTimeout(resolve, delay));
                    } else {
                        // Max retries exceeded for this series, but reset and try again
                        onProgress(`<div class="console-message console-warning">
<div class="console-message-header">RETRY LIMIT REACHED</div>
<div class="console-message-content">Failed ${maxRetries} attempts, resetting retry counter...</div>
<div class="console-message-content">Session: ${sessionId.substr(0, 8)}... | Continuing attempts...</div>
<div class="console-message-content">Starting fresh retry series with ${maxRetries} new attempts</div>
</div>`);

                        // Reset retry counter for this session
                        this.retryCounters.set(sessionId, 0);

                        // Wait a bit longer before resetting the retry series
                        await new Promise((resolve) => setTimeout(resolve, retryDelay * 2));
                        break; // Break inner loop to start fresh retry series
                    }
                }
            }
        }
    }

    /**
     * Make a streaming request (internal method)
     * @private
     */
    async _streamRequest(method, endpoint, data, onProgress, options = {}) {
        const authStore = useAuthStore();

        const defaultHeaders = {
            'Content-Type': 'application/json',
            Accept: 'text/plain',
            'Cache-Control': 'no-cache',
        };

        // Add session tracking headers if provided (for resilient mode)
        if (options.sessionId) {
            defaultHeaders['X-Stream-Session-Id'] = options.sessionId;
        }
        if (options.resumeAfter && options.resumeAfter > 0) {
            defaultHeaders['X-Stream-Resume-After'] = options.resumeAfter.toString();
        }

        // Add authorization if token exists
        if (authStore.token) {
            defaultHeaders.Authorization = `Bearer ${authStore.token}`;
        }

        const fetchOptions = {
            method: method,
            headers: { ...defaultHeaders, ...options.headers },
            signal: options.signal || null,
        };

        if (data && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
            fetchOptions.body = JSON.stringify(data);
        }

        let response;
        try {
            response = await fetch(this.baseURL + endpoint, fetchOptions);
        } catch (fetchError) {
            // Callers tell a deliberate stop from a failure by the name.
            if (fetchError?.name === 'AbortError') throw fetchError;
            console.error('Fetch error:', fetchError);
            throw new Error(`Network error: ${fetchError.message}`);
        }

        if (!response.ok) {
            const errorText = await response.text().catch(() => 'Unknown error');
            console.error('HTTP error response:', response.status, errorText);
            const httpError = new Error(`HTTP error! status: ${response.status} - ${errorText}`);
            httpError.status = response.status;
            httpError.responseText = errorText;
            throw httpError;
        }

        if (!response.body) {
            throw new Error('Streaming not supported by this browser or server');
        }

        return this._processStream(response.body, onProgress, options);
    }

    /**
     * Process a readable stream and count bytes
     * @private
     * @param {ReadableStream} stream - The response body stream
     * @param {Function} onProgress - Callback function for each chunk
     * @param {Object} options - Processing options
     * @returns {Promise<number>} Promise that resolves with total bytes processed
     */
    async _processStream(stream, onProgress, options = {}) {
        const reader = stream.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let totalBytes = 0;

        try {
            while (true) {
                let chunk;
                try {
                    chunk = await reader.read();
                } catch (readError) {
                    if (readError?.name === 'AbortError') throw readError;
                    console.error('Stream read error:', readError);

                    // Check if it's a recoverable network error
                    if (readError.name === 'TypeError' || readError.message.includes('network')) {
                        throw new Error(`Stream connection interrupted: ${readError.message}`);
                    }
                    throw new Error(`Stream read error: ${readError.message}`);
                }

                const { done, value } = chunk;
                if (done) break;

                totalBytes += value.byteLength;

                // Track raw bytes for resilient mode resume position
                if (options.sessionId && this.bytesReceived.has(options.sessionId)) {
                    this.bytesReceived.set(options.sessionId, (options.resumeAfter || 0) + totalBytes);
                }

                // Decode the chunk and add to buffer
                buffer += decoder.decode(value, { stream: true });

                // Process complete lines
                const lines = buffer.split('\n');
                buffer = lines.pop() || ''; // Keep incomplete line in buffer

                // Send each complete line to the progress callback. What it throws is the caller's own
                // error, so it propagates as thrown rather than as a read error.
                for (const line of lines) {
                    if (line.trim()) {
                        onProgress(line);

                        // Reset retry counter when we successfully receive data
                        // This indicates successful reconnection if we had previous retries
                        if (options.resilient && options.sessionId && this.retryCounters.has(options.sessionId)) {
                            const currentRetries = this.retryCounters.get(options.sessionId);
                            if (currentRetries > 0) {
                                this.retryCounters.set(options.sessionId, 0);
                            }
                        }
                    }
                }
            }

            // Process any remaining buffer content
            if (buffer.trim()) {
                onProgress(buffer);
            }

            return totalBytes;
        } catch (error) {
            console.error('Stream processing error:', error);
            throw error;
        } finally {
            try {
                reader.releaseLock();
            } catch (releaseError) {
                console.warn('Error releasing stream reader:', releaseError);
            }
        }
    }

    /**
     * Cancel an active streaming session (resilient mode)
     * @param {string} sessionId - Session ID to cancel
     */
    cancelSession(sessionId) {
        if (this.activeSessions.has(sessionId)) {
            this.activeSessions.delete(sessionId);
            this.retryCounters.delete(sessionId);
            this.bytesReceived.delete(sessionId);
            // Session cancelled
        }
    }

    /**
     * Get info about active sessions (resilient mode)
     * @returns {Array} Array of active session info
     */
    getActiveSessions() {
        return Array.from(this.activeSessions.entries()).map(([id, info]) => ({
            id,
            ...info,
            duration: Date.now() - info.startTime,
        }));
    }

    /**
     * Check the status of a background process (resilient mode)
     * @param {string} sessionId - Session ID to check
     * @returns {Promise<Object>} Process status
     */
    async checkProcessStatus(sessionId) {
        const authStore = useAuthStore();
        const headers = {
            'Content-Type': 'application/json',
        };

        if (authStore.token) {
            headers.Authorization = `Bearer ${authStore.token}`;
        }

        try {
            const response = await fetch(`${this.baseURL}/api/system/process-status/${sessionId}`, {
                method: 'GET',
                headers,
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error checking process status:', error);
            throw error;
        }
    }
}

// Create and export a singleton instance
const streamService = new StreamService();
export default streamService;
